import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import Drawer from "react-modern-drawer";

import {
    fetchFundingRequestClaims,
    fetchFundingRequestDetail,
} from "../../utils/apiUtils";
import { toWords } from "../../utils/commonUtils";
import { FaDollarSign, FaSlidersH } from "react-icons/fa";
import ApplyPaymentPanel from "./ApplyPaymentPanel";
import ApplyAdjustmentPanel from "./ApplyAdjustmentPanel";
import { refreshFundingAfterPayment, refreshFundingAfterAdjustment } from "./applyPaymentRefresh";
import "./FundingRequestDetail.scss";

const CLAIMS_PAGE_SIZE = 50;
const APPLY_DRAWER_SIZE = 480;
const TABS = [
    { id: "summary", label: "Summary" },
    { id: "claims", label: "Claims" },
    { id: "payments", label: "Employer payments" },
    { id: "adjustments", label: "Adjustments" },
];

const money = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
};

const moneyInWords = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return null;
    try {
        const num = Number(n);
        const words = toWords.convert(Math.abs(num), { currency: true });
        if (!words) return null;
        if (num < 0) return `Negative ${words.charAt(0).toLowerCase()}${words.slice(1)}`;
        return words;
    } catch {
        return null;
    }
};

const MoneyAmount = ({ value, className = "num" }) => {
    if (value == null || !Number.isFinite(Number(value))) {
        return <span className={className}>—</span>;
    }
    const tip = moneyInWords(value);
    return (
        <span
            className={`${className} funding-request-detail__money`.trim()}
            {...(tip ? { "data-tip": tip, title: tip } : {})}
        >
            {money(value)}
        </span>
    );
};

const accountBillsHref = (fr) => {
    if (!fr?.accountKey) return "/finance/bills";
    const params = new URLSearchParams();
    params.set("accountKey", String(fr.accountKey));
    if (fr.accountHccId) params.set("accountHccId", fr.accountHccId);
    if (fr.accountName) params.set("accountName", fr.accountName);
    return `/finance/bills?${params.toString()}`;
};

const accountFundingHref = (fr) => {
    if (!fr?.accountKey) return "/finance/funding-requests";
    const params = new URLSearchParams();
    params.set("accountKey", String(fr.accountKey));
    if (fr.accountHccId) params.set("accountHccId", fr.accountHccId);
    if (fr.accountName) params.set("accountName", fr.accountName);
    return `/finance/funding-requests?${params.toString()}`;
};

const accountParams = (fr) => {
    const params = new URLSearchParams();
    if (fr?.accountKey) params.set("accountKey", String(fr.accountKey));
    if (fr?.accountHccId) params.set("accountHccId", fr.accountHccId);
    if (fr?.accountName) params.set("accountName", fr.accountName);
    return params;
};

const paymentCyclesHref = (fr) => {
    const params = accountParams(fr);
    const qs = params.toString();
    return `/finance/payment-cycles${qs ? `?${qs}` : ""}`;
};

const providerPaymentsHref = (fr) => {
    const params = accountParams(fr);
    const cycle = fr?.paymentCycleId || fr?.cycleKey;
    if (cycle) params.set("paymentCycleKey", String(cycle));
    const qs = params.toString();
    return `/finance/payments${qs ? `?${qs}` : ""}`;
};

const FundingRequestDetail = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { fundingRequestFactKey } = useParams();
    const [searchParams] = useSearchParams();
    const asOf = searchParams.get("asOf") || undefined;
    const [tab, setTab] = useState("summary");
    const [panel, setPanel] = useState(null);
    const closePanel = () => setPanel(null);
    const openApplyPayment = () => setPanel({ type: "apply-payment" });
    const openApplyAdjustment = () => setPanel({ type: "apply-adjustment" });

    const { data, isLoading, error } = useQuery({
        queryKey: ["funding-request-detail", fundingRequestFactKey, asOf || null],
        queryFn: () =>
            fetchFundingRequestDetail({
                fundingRequestFactKey,
                asOfDate: asOf,
            }),
        enabled: !!fundingRequestFactKey,
        staleTime: 30_000,
        retry: false,
    });

    const [claimsScrollRoot, setClaimsScrollRoot] = useState(null);
    const { ref: loadMoreRef, inView: loadMoreInView } = useInView({
        root: claimsScrollRoot,
        rootMargin: "120px",
        threshold: 0,
    });

    const {
        data: claimsData,
        isLoading: claimsLoading,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        error: claimsError,
    } = useInfiniteQuery({
        queryKey: ["funding-request-claims", fundingRequestFactKey],
        queryFn: ({ pageParam = 0 }) =>
            fetchFundingRequestClaims({
                fundingRequestFactKey,
                limit: CLAIMS_PAGE_SIZE,
                offset: pageParam,
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const loaded = pages.reduce((sum, p) => sum + (p?.claims?.length || 0), 0);
            const total = lastPage?.total ?? 0;
            if (loaded >= total) return undefined;
            if ((lastPage?.claims?.length || 0) < CLAIMS_PAGE_SIZE) return undefined;
            return loaded;
        },
        enabled: !!fundingRequestFactKey && tab === "claims",
        staleTime: 30_000,
        retry: false,
    });

    useEffect(() => {
        if (tab === "claims" && loadMoreInView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [tab, loadMoreInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const fr = data?.fundingRequest;
    const payments = Array.isArray(data?.payments) ? data.payments : [];
    const adjustments = Array.isArray(data?.adjustments) ? data.adjustments : [];
    const claims = useMemo(
        () => (claimsData?.pages || []).flatMap((p) => p?.claims || []),
        [claimsData],
    );
    const claimsTotal = Number(claimsData?.pages?.[0]?.total) || 0;
    const claimsAmount = useMemo(
        () => claims.reduce((sum, c) => sum + (Number(c.amount) || 0), 0),
        [claims],
    );

    if (isLoading) {
        return (
            <div className="container funding-request-detail scrollable-form">
                <p className="muted">Loading funding request…</p>
            </div>
        );
    }

    if (error || data?.error || !fr) {
        return (
            <div className="container funding-request-detail scrollable-form">
                <p className="danger">
                    {error?.message || data?.error || "Funding request not found"}
                </p>
                <button
                    type="button"
                    className="funding-request-detail__link"
                    onClick={() => navigate("/finance/funding-requests")}
                >
                    Back to Funding Requests
                </button>
            </div>
        );
    }

    const totalDue = Number(fr.totalDue) || 0;
    const totalPayments = Number(fr.totalPayments) || 0;

    return (
        <div
            className={`container funding-request-detail scrollable-form${
                tab === "claims" ? " funding-request-detail--claims" : ""
            }`}
        >
            <div className="funding-request-detail__toolbar">
                <div className="funding-request-detail__toolbar-left">
                    <Link
                        to={accountFundingHref(fr)}
                        className="funding-request-detail__link"
                    >
                        ← Funding Requests
                    </Link>
                    <div>
                        <p className="title">Funding request #{fr.key}</p>
                        <p className="sub-title">
                            {fr.accountName || fr.accountHccId || "Account"}
                            {fr.accountHccId ? ` · ${fr.accountHccId}` : ""}
                            {" · "}
                            {fr.statusLabel || fr.status || "—"}
                            {fr.paidStatus ? ` · ${fr.paidStatus}` : ""}
                        </p>
                    </div>
                </div>
                <div className="funding-request-detail__toolbar-actions">
                    <button
                        type="button"
                        className="btn funding-request-detail__btn funding-request-detail__btn--apply"
                        onClick={() =>
                            panel?.type === "apply-payment" ? closePanel() : openApplyPayment()
                        }
                        aria-pressed={panel?.type === "apply-payment"}
                    >
                        <FaDollarSign aria-hidden />
                        Apply payment
                    </button>
                    <button
                        type="button"
                        className="btn funding-request-detail__btn funding-request-detail__btn--adjust"
                        onClick={() =>
                            panel?.type === "apply-adjustment"
                                ? closePanel()
                                : openApplyAdjustment()
                        }
                        aria-pressed={panel?.type === "apply-adjustment"}
                    >
                        <FaSlidersH aria-hidden />
                        Apply adjustment
                    </button>
                    {!!(fr.paymentCycleId || fr.cycleKey) && (
                        <Link
                            to={providerPaymentsHref(fr)}
                            className="btn funding-request-detail__btn"
                        >
                            Provider payments on cycle
                        </Link>
                    )}
                    {!!fr.accountKey && (
                        <Link
                            to={paymentCyclesHref(fr)}
                            className="btn funding-request-detail__btn"
                        >
                            Payment cycles
                        </Link>
                    )}
                    {!!fr.accountKey && (
                        <Link
                            to={accountBillsHref(fr)}
                            className="btn funding-request-detail__btn"
                        >
                            Premium bills
                        </Link>
                    )}
                </div>
            </div>

            <article className="funding-request-detail__card">
                <section className="funding-request-detail__summary">
                    <div>
                        <span className="funding-request-detail__meta-label">Created</span>
                        <strong>{fr.created || "—"}</strong>
                    </div>
                    <div>
                        <span className="funding-request-detail__meta-label">Due</span>
                        <strong>{fr.dueDate || "—"}</strong>
                    </div>
                    <div>
                        <span className="funding-request-detail__meta-label">Payment cycle</span>
                        <strong className="funding-request-detail__cycle">
                            {fr.paymentCycleId || fr.cycleKey || "—"}
                        </strong>
                    </div>
                    <div>
                        <span className="funding-request-detail__meta-label">Claim amount</span>
                        <MoneyAmount value={fr.claimAmount} className="" />
                    </div>
                    <div>
                        <span className="funding-request-detail__meta-label">
                            Employer payments
                        </span>
                        <MoneyAmount value={fr.totalPayments} className="" />
                    </div>
                    <div className="funding-request-detail__summary-due">
                        <span className="funding-request-detail__meta-label">Total due</span>
                        <MoneyAmount value={fr.totalDue} className="" />
                    </div>
                </section>

                <p className="funding-request-detail__nav-story">
                    <strong>Inbound:</strong> this funding request (employer cash call) →{" "}
                    <strong>Outbound:</strong>{" "}
                    <Link to={providerPaymentsHref(fr)}>provider payments</Link> on cycle{" "}
                    <Link to={paymentCyclesHref(fr)}>
                        {fr.paymentCycleId || fr.cycleKey || "payment cycle"}
                    </Link>
                    . Premium invoice Claims Funding float is separate — see{" "}
                    <Link to={accountBillsHref(fr)}>premium bills</Link>.
                </p>

                <div className="funding-request-detail__tabs" role="tablist">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === t.id}
                            className={`funding-request-detail__tab${
                                tab === t.id ? " funding-request-detail__tab--active" : ""
                            }`}
                            onClick={() => setTab(t.id)}
                        >
                            {t.label}
                            {t.id === "claims" && fr.claimCount != null
                                ? ` (${fr.claimCount})`
                                : ""}
                            {t.id === "adjustments" && adjustments.length
                                ? ` (${adjustments.length})`
                                : ""}
                        </button>
                    ))}
                </div>

                {tab === "summary" && (
                    <div className="funding-request-detail__tab-panel">
                        <p className="funding-request-detail__lead">
                            This is a cash call to fund claim payables — not the premium invoice
                            “Claims Funding” rate lines. Creating or approving this request does
                            <strong> not</strong> automatically withdraw from the account bank;
                            payment is a separate step.
                        </p>
                        <dl className="funding-request-detail__fields">
                            <div>
                                <dt>Status</dt>
                                <dd>{fr.statusLabel || fr.status || "—"}</dd>
                            </div>
                            <div>
                                <dt>Claims</dt>
                                <dd>{fr.claimCount ?? "—"}</dd>
                            </div>
                            <div>
                                <dt>Admin charges</dt>
                                <dd>
                                    <MoneyAmount value={fr.adminCharges} className="" />
                                </dd>
                            </div>
                            <div>
                                <dt>External fees</dt>
                                <dd>
                                    <MoneyAmount value={fr.externalFees} className="" />
                                </dd>
                            </div>
                            <div>
                                <dt>Total charges</dt>
                                <dd>
                                    <MoneyAmount value={fr.totalCharges} className="" />
                                </dd>
                            </div>
                            <div>
                                <dt>Adjustments</dt>
                                <dd>
                                    <MoneyAmount value={fr.totalAdjustments} className="" />
                                </dd>
                            </div>
                            <div>
                                <dt>Balance forward</dt>
                                <dd>
                                    <MoneyAmount value={fr.balanceForward} className="" />
                                </dd>
                            </div>
                        </dl>
                    </div>
                )}

                {tab === "claims" && (
                    <section className="funding-request-detail__claims">
                        <div className="funding-request-detail__claims-head">
                            <h2>Claims on this request</h2>
                            {!!claims.length && (
                                <span className="funding-request-detail__muted">
                                    {claims.length}
                                    {claimsTotal > claims.length ? ` / ${claimsTotal}` : ""}
                                    {" · "}
                                    {money(claimsAmount)}
                                </span>
                            )}
                        </div>

                        {claimsLoading && (
                            <p className="funding-request-detail__muted">Loading claims…</p>
                        )}
                        {claimsError && (
                            <p className="danger">
                                {claimsError?.message || "Unable to load claims."}
                            </p>
                        )}
                        {!claimsLoading && !claimsError && !claims.length && (
                            <p className="funding-request-detail__muted">
                                No claims on this request.
                            </p>
                        )}

                        {!!claims.length && (
                            <>
                                <div
                                    className="funding-request-detail__colhead"
                                    aria-hidden="true"
                                >
                                    <span>Claim / member</span>
                                    <span>Status</span>
                                    <span className="num">Amount</span>
                                </div>
                                <div
                                    className="funding-request-detail__table-scroll"
                                    ref={setClaimsScrollRoot}
                                >
                                    <table className="funding-request-detail__table">
                                        <tbody>
                                            {claims.map((c) => (
                                                <tr key={c.key}>
                                                    <td>
                                                        <div className="funding-request-detail__claim-id">
                                                            {c.claimHccId || "—"}
                                                        </div>
                                                        <div className="funding-request-detail__muted">
                                                            {c.memberName || "—"}
                                                            {c.memberHccId
                                                                ? ` · ${c.memberHccId}`
                                                                : ""}
                                                        </div>
                                                    </td>
                                                    <td>{c.claimStatus || "—"}</td>
                                                    <td className="num">
                                                        <MoneyAmount value={c.amount} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div
                                        ref={loadMoreRef}
                                        className="funding-request-detail__sentinel"
                                    >
                                        {isFetchingNextPage
                                            ? "Loading more…"
                                            : hasNextPage
                                              ? "Scroll for more"
                                              : "End of list"}
                                    </div>
                                </div>
                            </>
                        )}
                    </section>
                )}

                {tab === "adjustments" && (
                    <div className="funding-request-detail__tab-panel">
                        <p className="funding-request-detail__lead">
                            <strong>Adjustments</strong> allocated to this funding request
                            (or matching account + payment cycle). Header totals can lag —
                            line amounts below are the source of truth when present.
                        </p>
                        <dl className="funding-request-detail__fields funding-request-detail__fields--payments">
                            <div>
                                <dt>Adjustments total</dt>
                                <dd>
                                    <MoneyAmount value={fr.totalAdjustments} className="" />
                                </dd>
                            </div>
                            <div>
                                <dt>Lines</dt>
                                <dd>{adjustments.length}</dd>
                            </div>
                        </dl>
                        {adjustments.length === 0 ? (
                            <p className="funding-request-detail__muted">
                                No adjustments on this request yet.
                            </p>
                        ) : (
                            <div className="funding-request-detail__table-scroll">
                                <table className="funding-request-detail__table">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th>Created</th>
                                            <th>Source</th>
                                            <th className="num">Amount</th>
                                            <th>Comments</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {adjustments.map((a) => (
                                            <tr key={a.key}>
                                                <td>
                                                    <div>{a.category || "—"}</div>
                                                    {a.categoryCode ? (
                                                        <div className="funding-request-detail__muted">
                                                            {a.categoryCode}
                                                        </div>
                                                    ) : null}
                                                </td>
                                                <td>{a.created || "—"}</td>
                                                <td className="funding-request-detail__muted">
                                                    {a.isSystemGenerated
                                                        ? "System"
                                                        : "Manual"}
                                                    {a.adjustmentPayableKey
                                                        ? ` · payable ${a.adjustmentPayableKey}`
                                                        : ""}
                                                    {a.manualReceivableKey
                                                        ? ` · receivable ${a.manualReceivableKey}`
                                                        : ""}
                                                </td>
                                                <td className="num">
                                                    <MoneyAmount value={a.amount} className="num" />
                                                </td>
                                                <td className="funding-request-detail__muted">
                                                    {a.comments || "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {tab === "payments" && (
                    <div className="funding-request-detail__tab-panel">
                        <p className="funding-request-detail__lead">
                            <strong>Employer payments</strong> applied to this funding request
                            (cash call). This is not the provider check/EFT list — use{" "}
                            <Link to={providerPaymentsHref(fr)}>Provider payments on cycle</Link>{" "}
                            for outbound disbursements. Creating this request does not
                            auto-withdraw from the account bank.
                        </p>
                        <dl className="funding-request-detail__fields funding-request-detail__fields--payments">
                            <div>
                                <dt>Total due</dt>
                                <dd>
                                    <MoneyAmount value={fr.totalDue} className="" />
                                </dd>
                            </div>
                            <div>
                                <dt>Payments applied</dt>
                                <dd>
                                    <MoneyAmount value={fr.totalPayments} className="" />
                                </dd>
                            </div>
                            <div>
                                <dt>Balance forward</dt>
                                <dd>
                                    <MoneyAmount value={fr.balanceForward} className="" />
                                </dd>
                            </div>
                            <div>
                                <dt>Remaining</dt>
                                <dd>
                                    <MoneyAmount
                                        value={Math.max(0, totalDue - totalPayments)}
                                        className=""
                                    />
                                    <div className="funding-request-detail__muted">
                                        total due minus payments applied
                                    </div>
                                </dd>
                            </div>
                        </dl>
                        {payments.length === 0 ? (
                            <p className="funding-request-detail__muted">
                                No payments applied on this request yet.
                            </p>
                        ) : (
                            <div className="funding-request-detail__table-scroll">
                                <table className="funding-request-detail__table">
                                    <thead>
                                        <tr>
                                            <th>Payment #</th>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th className="num">Amount</th>
                                            <th>Comments</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((p) => (
                                            <tr key={p.key || p.paymentNumber}>
                                                <td>{p.paymentNumber || "—"}</td>
                                                <td>{p.paymentDate || p.receiptDate || "—"}</td>
                                                <td>{p.paymentType || "—"}</td>
                                                <td className="num">
                                                    <MoneyAmount value={p.amount} className="num" />
                                                </td>
                                                <td className="funding-request-detail__muted">
                                                    {p.comments || "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </article>

            <Drawer
                open={!!panel}
                onClose={closePanel}
                direction="right"
                size={APPLY_DRAWER_SIZE}
                duration={200}
                className="funding-request-detail__drawer"
                overlayClassName="funding-request-detail__drawer-overlay"
            >
                <div className="funding-request-detail__panel">
                    <header className="funding-request-detail__panel-head">
                        <div>
                            <p className="funding-request-detail__panel-kicker">Submit to HRP</p>
                            <h2>
                                {panel?.type === "apply-adjustment"
                                    ? "Apply adjustment"
                                    : "Apply payment"}
                            </h2>
                        </div>
                        <button
                            type="button"
                            className="funding-request-detail__panel-close"
                            onClick={closePanel}
                        >
                            Close
                        </button>
                    </header>
                    <div className="funding-request-detail__panel-body">
                        {panel?.type === "apply-payment" ? (
                            <ApplyPaymentPanel
                                key={`pay-${fr.key}`}
                                variant="drawer"
                                kind="funding"
                                context={{
                                    key: fr.key,
                                    fundingRequestId: fr.key,
                                    accountKey: fr.accountKey,
                                    accountHccId: fr.accountHccId,
                                    accountName: fr.accountName,
                                    paymentCycleId: fr.paymentCycleId || fr.cycleKey,
                                    amountDue: Math.max(0, totalDue - totalPayments),
                                }}
                                onClose={closePanel}
                                onApplied={async (result) => {
                                    setTab("payments");
                                    const status = await refreshFundingAfterPayment({
                                        queryClient,
                                        fundingRequestFactKey,
                                        asOf,
                                        result,
                                        fetchDetail: fetchFundingRequestDetail,
                                    });
                                    queryClient.invalidateQueries({
                                        queryKey: ["account-funding-overview"],
                                    });
                                    queryClient.invalidateQueries({
                                        queryKey: ["funding-requests"],
                                    });
                                    return status;
                                }}
                            />
                        ) : null}
                        {panel?.type === "apply-adjustment" ? (
                            <ApplyAdjustmentPanel
                                key={`adj-${fr.key}`}
                                variant="drawer"
                                kind="funding"
                                context={{
                                    key: fr.key,
                                    fundingRequestId: fr.key,
                                    accountKey: fr.accountKey,
                                    accountHccId: fr.accountHccId,
                                    accountName: fr.accountName,
                                    paymentCycleId: fr.paymentCycleId || fr.cycleKey,
                                    bankAccountName: fr.accountHccId,
                                }}
                                onClose={closePanel}
                                onApplied={async (result) => {
                                    setTab("adjustments");
                                    const status = await refreshFundingAfterAdjustment({
                                        queryClient,
                                        fundingRequestFactKey,
                                        asOf,
                                        result,
                                        fetchDetail: fetchFundingRequestDetail,
                                    });
                                    queryClient.invalidateQueries({
                                        queryKey: ["account-funding-overview"],
                                    });
                                    queryClient.invalidateQueries({
                                        queryKey: ["funding-requests"],
                                    });
                                    return status;
                                }}
                            />
                        ) : null}
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default FundingRequestDetail;
